import axios from 'axios';
import { AuthUser, Team, PokerResult, ResultVote } from '../types/team';
import { API_V1_URL } from '../config/apiConfig';

const AUTH_TOKEN_KEY = 'auth_token';
const AUTH_USER_KEY = 'auth_user';

const client = axios.create({
  baseURL: API_V1_URL,
  headers: { 'Content-Type': 'application/json' },
});

// リクエストごとにトークンを付与する
client.interceptors.request.use((config) => {
  const token = localStorage.getItem(AUTH_TOKEN_KEY);
  if (token) {
    config.headers = config.headers || {};
    (config.headers as any)['Authorization'] = `Bearer ${token}`;
  }
  return config;
});

// ---- 認証状態のヘルパー ----

export const authStore = {
  getToken(): string | null {
    return localStorage.getItem(AUTH_TOKEN_KEY);
  },

  getUser(): AuthUser | null {
    const raw = localStorage.getItem(AUTH_USER_KEY);
    if (!raw) return null;
    try {
      return JSON.parse(raw) as AuthUser;
    } catch {
      return null;
    }
  },

  isLoggedIn(): boolean {
    return !!localStorage.getItem(AUTH_TOKEN_KEY);
  },

  setAuth(token: string, user: AuthUser): void {
    localStorage.setItem(AUTH_TOKEN_KEY, token);
    localStorage.setItem(AUTH_USER_KEY, JSON.stringify(user));
    window.dispatchEvent(new Event('auth-changed'));
  },

  clearAuth(): void {
    localStorage.removeItem(AUTH_TOKEN_KEY);
    localStorage.removeItem(AUTH_USER_KEY);
    window.dispatchEvent(new Event('auth-changed'));
  },
};

// ---- API ----

export const accountApi = {
  register: async (email: string, password: string, displayName: string) => {
    const res = await client.post<{ token: string; user: AuthUser }>('/auth/register', {
      email,
      password,
      display_name: displayName,
    });
    authStore.setAuth(res.data.token, res.data.user);
    return res.data;
  },

  login: async (email: string, password: string) => {
    const res = await client.post<{ token: string; user: AuthUser }>('/auth/login', {
      email,
      password,
    });
    authStore.setAuth(res.data.token, res.data.user);
    return res.data;
  },

  logout: async () => {
    try {
      await client.post('/auth/logout');
    } finally {
      authStore.clearAuth();
    }
  },

  forgotPassword: async (email: string): Promise<void> => {
    await client.post('/auth/forgot-password', { email });
  },

  resetPassword: async (token: string, password: string): Promise<void> => {
    await client.post('/auth/reset-password', { token, password });
  },

  me: async (): Promise<AuthUser> => {
    const res = await client.get<{ user: AuthUser }>('/auth/me');
    return res.data.user;
  },

  createTeam: async (name: string): Promise<Team> => {
    const res = await client.post<{ team: Team }>('/teams', { name });
    return res.data.team;
  },

  joinTeam: async (inviteCode: string): Promise<Team> => {
    const res = await client.post<{ team: Team }>('/teams/join', { invite_code: inviteCode });
    return res.data.team;
  },

  listTeams: async (): Promise<Team[]> => {
    const res = await client.get<{ teams: Team[] }>('/teams');
    return res.data.teams;
  },

  getTeam: async (teamId: string): Promise<Team> => {
    const res = await client.get<{ team: Team }>(`/teams/${teamId}`);
    return res.data.team;
  },

  recordResult: async (
    teamId: string,
    data: {
      room_id: string;
      session_id: string;
      story_title: string;
      story_description?: string;
      votes: ResultVote[];
    }
  ): Promise<PokerResult> => {
    const res = await client.post<{ result: PokerResult }>(`/teams/${teamId}/results`, data);
    return res.data.result;
  },

  listResults: async (teamId: string): Promise<PokerResult[]> => {
    const res = await client.get<{ results: PokerResult[] }>(`/teams/${teamId}/results`);
    return res.data.results;
  },
};

export default accountApi;
